import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  OnInit,
  Output,
  ViewChild,
  computed,
  inject,
  signal,
  viewChildren,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent } from '@angular/material/dialog';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { FocusService } from 'app/core/services/focus.service';
import { HighlightService } from 'app/core/services/highlight.service';
import { ComboModel } from 'app/shared/components/combo/combo-model';
import { ComboComponent } from 'app/shared/components/combo/combo.component';
import { GridFilterModel } from 'app/shared/components/grid-filter/grid-filter-model';
import { GridFilterComponent } from 'app/shared/components/grid-filter/grid-filter.component';
import { RowElementComponent } from 'app/shared/components/rows/row-element/row-element.component';
import { TableConfig } from 'app/shared/components/table-wrapper/table-config';
import { TableWrapperComponent } from 'app/shared/components/table-wrapper/table-wrapper.component';
import { PickerState } from '../../../../states/picker-state';
import { ElementModel } from '../element.model';
import { PickerDialogModel } from './picker-dialog-model';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { GridFilterComponent as GridFilterComponent_1 } from '../../../grid-filter/grid-filter.component';
import { ComboComponent as ComboComponent_1 } from '../../../combo/combo.component';
import { ListResultComponent } from '../../../list-result/list-result.component';
import { TableWrapperComponent as TableWrapperComponent_1 } from '../../../table-wrapper/table-wrapper.component';
import { RowElementComponent as RowElementComponent_1 } from '../../../rows/row-element/row-element.component';
import { NgClass } from '@angular/common';
import { GroupedLabelCardComponent } from '../../../cards/grouped-label-card/grouped-label-card.component';
import { MatProgressBar } from '@angular/material/progress-bar';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-picker-dialog',
  styleUrls: ['./picker-dialog.component.scss'],
  templateUrl: './picker-dialog.component.html',
  imports: [
    CdkDrag,
    CdkDragHandle,
    MatDialogTitle,

    MatIconButton,
    MatTooltip,
    MatIcon,
    CdkScrollable,
    MatDialogContent,

    GridFilterComponent_1,

    ComboComponent_1,
    ListResultComponent,
    TableWrapperComponent_1,
    RowElementComponent_1,
    NgClass,

    GroupedLabelCardComponent,
    MatProgressBar,
    MatPaginator,
  ],
})
export class PickerDialogComponent implements OnInit {
  @Output() public selectElement = new EventEmitter<ElementModel>();
  @Output() public updateState = new EventEmitter<PickerState>();
  @ViewChild('_combo2Component') private _combo2Component: ComboComponent;
  @ViewChild('_comboComponent') private _comboComponent: ComboComponent;
  @ViewChild('_gridFilterComponent', { static: true }) private _gridFilterComponent: GridFilterComponent;
  @ViewChild('_tableWrapperComponent') private _tableWrapperComponent: TableWrapperComponent;
  public elements = signal<ElementModel[]>([]);
  public gridFilter: GridFilterModel;
  public gridFilter2: GridFilterModel;
  public loadedFlag = signal(false);
  public pickerDialog: PickerDialogModel;
  public shouldBeFirstSerialNumber = false;
  public tableConfig = computed(() => new TableConfig([...this._rowElements()]));
  public totalCount = signal(0);

  private _focusService = inject(FocusService);
  private _highlightService = inject(HighlightService);
  private _rowElements = viewChildren(RowElementComponent);
  private dialogRef = inject<MatDialogRef<PickerDialogComponent>>(MatDialogRef);

  public constructor() {
    const data = inject(MAT_DIALOG_DATA);

    this.pickerDialog = data;
    this.shouldBeFirstSerialNumber = true;
  }

  public changeSelectedContainer(selectedComboModel: ComboModel): void {
    delete this.pickerDialog.state.filterSelectedContainerUuid;
    delete this.pickerDialog.state.filterSelectedSubcontainerUuid;

    if (selectedComboModel) {
      if (selectedComboModel.parent) {
        this.pickerDialog.state.filterSelectedSubcontainerUuid = selectedComboModel.key;
      } else {
        this.pickerDialog.state.filterSelectedContainerUuid = selectedComboModel.key;
      }
    }

    this.updateAndEmitState();
  }

  public chooseElement(element: ElementModel): boolean {
    element.firstSerialNumberFlag = false;
    if (this.shouldBeFirstSerialNumber) {
      this.shouldBeFirstSerialNumber = false;
      element.firstSerialNumberFlag = true;
    }

    this.selectElement.emit(element);
    if (this.pickerDialog.unpinnedByDefaultFlag) {
      this.dialogRef.close();
    }
    return false;
  }

  public close(): void {
    this.dialogRef.close();
  }

  public combo2SelectedChangeHandler(selectedComboModel: ComboModel): void {
    delete this.pickerDialog.state.filter2SelectedContainerUuid;

    if (selectedComboModel) {
      this.pickerDialog.state.filter2SelectedContainerUuid = selectedComboModel.key;
    }

    this.updateAndEmitState();
  }

  public elementCreationLinkClickedEventHandler(): void {
    this.dialogRef.close();
  }

  public focusOnTable(): void {
    this._focusService.setTimeoutOutsideAngular(() => {
      this._tableWrapperComponent.focusAtIndex(0);
    });
  }

  public focusOnTextFilter(): void {
    this._gridFilterComponent?.setFocus();
  }

  public isComboModelSelected(comboModel: ComboModel): boolean {
    if (!comboModel) {
      if (!this.pickerDialog.state.filterSelectedSubcontainerUuid && !this.pickerDialog.state.filterSelectedContainerUuid) {
        return true;
      }
      return false;
    }
    if (comboModel.parent && this.pickerDialog.state.filterSelectedSubcontainerUuid === comboModel.key) {
      return true;
    }
    if (!comboModel.parent && this.pickerDialog.state.filterSelectedContainerUuid === comboModel.key) {
      return true;
    }
    return false;
  }

  public keydownArrowLeftEventHandler(): void {
    if (this._tableWrapperComponent) {
      this._tableWrapperComponent.indexFocusedRow = null;
    }
    this.updateState.emit(this.pickerDialog.state);
  }

  public keydownArrowRightEventHandler(): void {
    if (this._tableWrapperComponent) {
      this._tableWrapperComponent.indexFocusedRow = null;
    }
    this.updateState.emit(this.pickerDialog.state);
  }

  @HostListener('window:keydown.control', ['$event'])
  public keydownCtrl(event: Event): void {
    if (this.pickerDialog.multipleSelectionFlag) {
      if ((event as KeyboardEvent).repeat) {
        return;
      }
      this.pickerDialog.unpinnedByDefaultFlag = false;
    }
  }

  @HostListener('window:keyup.control')
  public keyupCtrl(): void {
    if (this.pickerDialog.multipleSelectionFlag) {
      this.pickerDialog.unpinnedByDefaultFlag = true;
    }
  }

  public ngOnInit(): void {
    this._initTextFilter(this.pickerDialog.state.textFilter);
    this._initTextFilter2(this.pickerDialog.state.textFilter2);
    if (this.pickerDialog.comboDefaultValue && this._comboComponent) {
      this._comboComponent.keySelected = this.pickerDialog.comboDefaultValue.key;
      this.dialogRef.componentInstance.pickerDialog.state.filterSelectedContainerUuid = this.pickerDialog.comboDefaultValue.key;
    }

    if (this.pickerDialog.combo2DefaultValue && this._combo2Component) {
      this._combo2Component.keySelected = this.pickerDialog.combo2DefaultValue.key;
      this.dialogRef.componentInstance.pickerDialog.state.filter2SelectedContainerUuid = this.pickerDialog.combo2DefaultValue.key;
    }

    // Trigger the search if there are initial filter values
    if (this.pickerDialog.state.textFilter || this.pickerDialog.state.textFilter2) {
      this.updateAndEmitState();
    }
  }

  public setCloseModeHandler(): void {
    this.pickerDialog.unpinnedByDefaultFlag = !this.pickerDialog.unpinnedByDefaultFlag;
  }

  public textFilter2ChangedEventHandler(newTextFilter: string): void {
    this.pickerDialog.state.textFilter2 = newTextFilter;
    this._initTextFilter2(this.pickerDialog.state.textFilter2);

    this.updateAndEmitState();
  }

  public textFilterChangedEventHandler(newTextFilter: string): void {
    this.pickerDialog.state.textFilter = newTextFilter;
    this._initTextFilter(this.pickerDialog.state.textFilter);

    this.updateAndEmitState();
  }

  public updateAndEmitState(): void {
    this.pickerDialog.state.pageIndex = 0;

    // Here, a new object has to be assigned to pickerDialog
    // for raise ngOnChanges on app-list-result that has pickerDialog.state as an input.
    const state = Object.assign(new PickerState(), this.pickerDialog.state);
    this.pickerDialog.state = state;

    this.updateState.emit(this.pickerDialog.state);
  }

  public updatePaginator(pageEvent: PageEvent): void {
    this.pickerDialog.state.pageIndex = pageEvent.pageIndex;
    this.pickerDialog.state.pageSize = pageEvent.pageSize;
    this.updateState.emit(this.pickerDialog.state);
  }

  private _initTextFilter(textFilter: string | null | undefined): void {
    this.gridFilter = new GridFilterModel(textFilter);
    this._highlightService.compileFilter(textFilter);
  }

  private _initTextFilter2(textFilter2: string | null): void {
    this.gridFilter2 = new GridFilterModel(textFilter2);
    this._highlightService.compileFilter(textFilter2);
  }
}
